import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import { useConfig } from "../../../context/ConfigContext";
import { formatNumber } from "../../../components/utils/format";

const LiveImpactCounter = () => {
  const config = useConfig();
  const {
    ENERGIA_TOTAL,
    CO2_TOTAL,
    AHORRO_TOTAL,
    TOTAL_ARBOLES,
  } = config;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    const element = document.getElementById("impact-counter");
    if (element) {
      observer?.observe(element);
    }

    return () => {
      if (element) {
        observer?.unobserve(element);
      }
    };
  }, []);

  const impactStats = [
    {
      id: "energy",
      icon: "Zap",
      value: formatNumber(ENERGIA_TOTAL),
      unit: "Wh",
      label: "Energía Generada",
      description: "Energía limpia producida por todos nuestros sistemas",
      color: "text-accent",
      bgColor: "from-accent/10 to-accent/5",
      pulse: "animate-pulse",
    },
    {
      id: "co2",
      icon: "Leaf",
      value: formatNumber(CO2_TOTAL),
      unit: "Toneladas",
      label: "CO₂ Evitado",
      description: "Emisiones de carbono evitadas al ambiente",
      color: "text-success",
      bgColor: "from-success/10 to-success/5",
      pulse: "animate-pulse",
    },
    {
      id: "savings",
      icon: "DollarSign",
      value: `Q ${formatNumber(AHORRO_TOTAL)}`,
      unit: "",
      label: "Ahorros Generados",
      description: "Dinero ahorrado por nuestros clientes",
      color: "text-primary",
      bgColor: "from-primary/10 to-primary/5",
      pulse: "animate-pulse",
    },
    {
      id: "trees",
      icon: "Trees",
      value: formatNumber(TOTAL_ARBOLES),
      unit: "Árboles",
      label: "Equivalente Plantado",
      description: "Árboles equivalentes en captura de CO₂",
      color: "text-secondary",
      bgColor: "from-secondary/10 to-secondary/5",
      pulse: "animate-pulse",
    },
  ];

  return (
    <section
      id="impact-counter"
      className="py-16 bg-gradient-to-br from-muted/30 to-background"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-success/10 text-success px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Activity" size={16} />
            <span>Impacto Real</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
            El Poder Colectivo de la Energía Solar
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Cada segundo, nuestros sistemas solares generan energía limpia,
            reducen emisiones y crean ahorros reales para todas las familias
          </p>
        </div>

        {/* Live Counter Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {impactStats?.map((stat, index) => (
            <div
              key={stat?.id}
              className={`relative p-6 rounded-2xl bg-gradient-to-br ${stat?.bgColor} solar-shadow-card solar-hover-lift group overflow-hidden`}
              style={{ animationDelay: `${index * 200}ms` }}
            >
              {/* Background Pattern */}
              <div className="absolute top-0 right-0 w-20 h-20 opacity-5">
                <Icon name={stat?.icon} size={80} className={stat?.color} />
              </div>

              {/* Icon */}
              <div
                className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white solar-shadow-card mb-4 ${stat?.color}`}
              >
                <Icon name={stat?.icon} size={24} />
              </div>

              {/* Value */}
              <div className="mb-2">
                <div
                  className={`text-2xl sm:text-3xl font-bold ${stat?.color} ${isVisible ? stat?.pulse : ""
                    }`}
                >
                  {stat?.value}
                </div>
                {stat?.unit && (
                  <div className="text-sm text-text-secondary font-medium">
                    {stat?.unit}
                  </div>
                )}
              </div>

              {/* Label */}
              <h3 className="font-semibold text-text-primary mb-2">
                {stat?.label}
              </h3>
              <p className="text-sm text-text-secondary leading-relaxed">
                {stat?.description}
              </p>

              {/* Live Indicator */}
              <div className="absolute top-4 right-4 flex items-center space-x-1">
                <div className="w-2 h-2 bg-success rounded-full animate-ping"></div>
                <span className="text-xs text-success font-medium">LIVE</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LiveImpactCounter;
